/**
 * Hours & Jobs by Employee (api#1193): the report's rows, the per-employee
 * detail, and the text both screens and their exports print.
 *
 *   GET /reports/by-crew?dateFrom&dateTo                   -> CrewHoursRow[]
 *   GET /reports/by-crew/:userId/detail?dateFrom&dateTo    -> CrewHoursDetail
 *
 * EVERY FIGURE IS THE API'S. total_hours is field + shop added server-side,
 * and pay is the sum of the detail rows the API priced; nothing here adds,
 * subtracts or re-derives one. Reads are snake_case.
 *
 * Absent is not zero, in two spellings the API uses deliberately:
 *   - a null figure arrives with a `*_reason` saying why. Render `—` and the
 *     reason, never `$0.00` or `0 hrs`.
 *   - `pay` is ABSENT from the row (not null) for a role the API strips pay
 *     from. That is "you may not see this", so the column is not drawn at all.
 */
import { fmtCurrency, fmtHours } from '../display-labels';
import { formatCalendarDate } from '../calendar-date';
import { shiftClockText } from '../pay-stub-groups';

export type FieldHoursSource = 'tickets' | 'clock_ins';

/** The small label beside Field hrs. Exhaustive; an unknown source gets none. */
export const FIELD_HOURS_SOURCE_LABEL: Record<FieldHoursSource, string> = {
  tickets: 'From tickets',
  clock_ins: 'From clock-ins',
};

export function fieldHoursSourceLabel(v: unknown): string | null {
  return v === 'tickets' || v === 'clock_ins' ? FIELD_HOURS_SOURCE_LABEL[v] : null;
}

export interface CrewHoursRow {
  userId: string | null;
  name: string | null;
  title: string | null;
  jobsWorked: number | null;
  daysWorked: number | null;
  fieldHours: number | null;
  shopHours: number | null;
  totalHours: number | null;
  fieldHoursSource: string | null;
  unresolvedTicketCount: number | null;
  unapprovedShopHours: number | null;
  /** `undefined` when the API stripped pay for this role; `null` when unpriced. */
  pay: number | null | undefined;
  payReason: string | null;
  payNote: string | null;
}

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

export function readCrewHoursRow(r: any): CrewHoursRow {
  return {
    userId: str(r?.user_id),
    name: str(r?.name),
    title: str(r?.position_title),
    jobsWorked: num(r?.jobs_worked),
    daysWorked: num(r?.days_worked),
    fieldHours: num(r?.field_hours),
    shopHours: num(r?.shop_hours),
    totalHours: num(r?.total_hours),
    fieldHoursSource: str(r?.field_hours_source),
    unresolvedTicketCount: num(r?.unresolved_ticket_count),
    unapprovedShopHours: num(r?.unapproved_shop_hours),
    pay: r && 'pay' in r ? num(r.pay) : undefined,
    payReason: str(r?.pay_reason),
    payNote: str(r?.pay_note),
  };
}

/** "14.75 hrs", or `—`. */
export function hrs(v: number | null): string {
  return v === null ? '—' : `${fmtHours(v)} hrs`;
}

/** The detail page's URL, carrying the report's range. */
export function crewDetailPath(userId: string, from: string, to: string): string {
  const q = new URLSearchParams();
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  const qs = q.toString();
  return `/reports/employee-hours/${encodeURIComponent(userId)}${qs ? `?${qs}` : ''}`;
}

/* ─── The detail ─────────────────────────────────────────────────────────── */

export interface CrewDetailRow {
  kind: 'ticket' | 'clock_shift' | null;
  ticketNumber: string | null;
  jobNumber: string | null;
  customerName: string | null;
  /** The row's first day (`work_date` on a ticket, `date` on a shift). */
  date: string | null;
  endDate: string | null;
  multiDay: boolean;
  clockOutAt: string | null;
  clockInLocal: string | null;
  clockOutLocal: string | null;
  hours: number | null;
  hoursReason: string | null;
  rate: number | null;
  pay: number | null | undefined;
  payReason: string | null;
}

export interface CrewDetailSection {
  rows: CrewDetailRow[];
  count: number | null;
  hours: number | null;
  pay: number | null | undefined;
}

export interface CrewHoursDetail {
  employeeName: string | null;
  from: string | null;
  to: string | null;
  currency: string | null;
  timeZone: string | null;
  fieldHoursSource: string | null;
  jobsWorked: number | null;
  daysWorked: number | null;
  unresolvedTicketCount: number | null;
  unapprovedShopHours: number | null;
  payNote: string | null;
  field: CrewDetailSection;
  shop: CrewDetailSection;
  totalHours: number | null;
  totalPay: number | null | undefined;
}

function readDetailRow(r: any): CrewDetailRow {
  const kind = r?.kind === 'ticket' || r?.kind === 'clock_shift' ? r.kind : null;
  return {
    kind,
    ticketNumber: str(r?.ticket_number),
    jobNumber: str(r?.job_number),
    customerName: str(r?.customer_name),
    date: str(r?.work_date ?? r?.date),
    endDate: str(r?.end_date),
    multiDay: r?.multi_day === true,
    clockOutAt: str(r?.clock_out_at),
    clockInLocal: str(r?.clock_in_local),
    clockOutLocal: str(r?.clock_out_local),
    hours: num(r?.hours),
    hoursReason: str(r?.hours_reason),
    rate: num(r?.rate),
    pay: r && 'pay' in r ? num(r.pay) : undefined,
    payReason: str(r?.pay_reason),
  };
}

function readSection(s: any): CrewDetailSection {
  const sub = s?.subtotal;
  return {
    rows: Array.isArray(s?.rows) ? s.rows.map(readDetailRow) : [],
    count: num(sub?.count),
    hours: num(sub?.hours),
    pay: sub && 'pay' in sub ? num(sub.pay) : undefined,
  };
}

/** The response body (axios `res.data`), envelope included. */
export function readCrewHoursDetail(body: any): CrewHoursDetail {
  const d = body && typeof body === 'object' && 'data' in body ? body.data : body;
  return {
    employeeName: str(d?.employee?.name),
    from: str(d?.range?.from),
    to: str(d?.range?.to),
    currency: str(d?.currency),
    timeZone: str(d?.time_zone),
    fieldHoursSource: str(d?.field_hours_source),
    jobsWorked: num(d?.jobs_worked),
    daysWorked: num(d?.days_worked),
    unresolvedTicketCount: num(d?.unresolved_ticket_count),
    unapprovedShopHours: num(d?.unapproved_shop_hours),
    payNote: str(d?.pay_note),
    field: readSection(d?.field),
    shop: readSection(d?.shop),
    totalHours: num(d?.total?.hours),
    totalPay: d?.total && 'pay' in d.total ? num(d.total.pay) : undefined,
  };
}

/** Whether pay reached this reader at all (the API strips it per role). */
export function detailShowsPay(d: CrewHoursDetail): boolean {
  return d.totalPay !== undefined;
}

const day = (v: string | null) => formatCalendarDate(v, { month: 'short', day: 'numeric', year: 'numeric' });

/** "Mar 25, 2026", or "Mar 25 – Mar 26, 2026" for a ticket spanning days. */
export function detailDateText(r: CrewDetailRow): string {
  if (!r.date) return '—';
  if (r.kind === 'ticket' && r.multiDay && r.endDate && r.endDate !== r.date) {
    const start = formatCalendarDate(r.date, { month: 'short', day: 'numeric' });
    return `${start} – ${day(r.endDate) ?? r.endDate}`;
  }
  return day(r.date) ?? '—';
}

/** The reference cell: the ticket number, or a shift's wall-clock in and out. */
export function detailRefText(r: CrewDetailRow, timeZone: string | null): string {
  if (r.kind === 'clock_shift') {
    return shiftClockText({ date: r.date, clockOutAt: r.clockOutAt, clockInLocal: r.clockInLocal, clockOutLocal: r.clockOutLocal }, timeZone);
  }
  return r.ticketNumber ?? '—';
}

/** Hours, as "14.75 hrs × $25.00" on a priced shift; `—` when absent. */
export function detailHoursText(r: CrewDetailRow, money: (v: number | null) => string): string {
  if (r.hours === null) return '—';
  return r.rate !== null ? `${hrs(r.hours)} × ${money(r.rate)}` : hrs(r.hours);
}

/* ─── Exports ─────────────────────────────────────────────────────────────── */

export interface ExportTable {
  headers: string[];
  rows: string[][];
}

/** The report's table as the PDF / CSV / Excel print it. Absent is `—`. */
export function crewHoursExport(rows: CrewHoursRow[]): ExportTable {
  const withPay = rows.some((r) => r.pay !== undefined);
  const headers = ['Employee', 'Title', 'Jobs', 'Days', 'Field hrs', 'Field hrs from', 'Shop hrs', 'Unapproved shop hrs', 'Total hrs'];
  if (withPay) headers.push('Pay');
  return {
    headers,
    rows: rows.map((r) => {
      const line = [
        r.name ?? '—',
        r.title ?? '—',
        r.jobsWorked === null ? '—' : String(r.jobsWorked),
        r.daysWorked === null ? '—' : String(r.daysWorked),
        r.fieldHours === null ? '—' : fmtHours(r.fieldHours),
        fieldHoursSourceLabel(r.fieldHoursSource) ?? '—',
        r.shopHours === null ? '—' : fmtHours(r.shopHours),
        r.unapprovedShopHours === null ? '—' : fmtHours(r.unapprovedShopHours),
        r.totalHours === null ? '—' : fmtHours(r.totalHours),
      ];
      if (withPay) line.push(r.pay == null ? `—${r.payReason ? ` (${r.payReason})` : ''}` : fmtCurrency(r.pay));
      return line;
    }),
  };
}

/**
 * The detail page as one sheet: a Jobs block, a Shop block, each with its
 * subtotal, then the grand total. Every figure the API's.
 */
export function crewDetailExport(d: CrewHoursDetail): ExportTable {
  // `$`, as the report's own table and the pay stub print it.
  const money = (v: number | null) => fmtCurrency(v);
  const withPay = detailShowsPay(d);
  const headers = ['Section', 'Date', 'Ticket / shift', 'Job', 'Customer', 'Hours', 'Note'];
  if (withPay) headers.push('Pay');
  const line = (section: string, r: CrewDetailRow) => {
    const cells = [
      section,
      detailDateText(r),
      detailRefText(r, d.timeZone),
      r.jobNumber ?? '—',
      r.customerName ?? '—',
      detailHoursText(r, money),
      [r.hoursReason, r.pay === null ? r.payReason : null].filter(Boolean).join(' · ') || '',
    ];
    if (withPay) cells.push(r.pay == null ? '—' : money(r.pay));
    return cells;
  };
  const subtotal = (label: string, s: CrewDetailSection) => {
    const cells = [label, '', s.count === null ? '—' : `${s.count} ${s.count === 1 ? 'row' : 'rows'}`, '', '', hrs(s.hours), ''];
    if (withPay) cells.push(s.pay == null ? '—' : money(s.pay));
    return cells;
  };
  const total = ['Grand total', '', '', '', '', hrs(d.totalHours), d.payNote ?? ''];
  if (withPay) total.push(d.totalPay == null ? '—' : money(d.totalPay));
  return {
    headers,
    rows: [
      ...d.field.rows.map((r) => line('Jobs', r)),
      subtotal('Jobs subtotal', d.field),
      ...d.shop.rows.map((r) => line('Shop', r)),
      subtotal('Shop subtotal', d.shop),
      total,
    ],
  };
}
